import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TrackedIconButton as IconButton } from '../../../components/TrackedComponents';
import useProfile from '../../../hooks/profile';
import palette from '../../ColorPalette';
import UniformInCabinGalleryThumb from './UniformInCabinGalleryThumb';
import {
    fileNameFromImageUrl,
    formatUniformCaptureUnixForDisplay,
    parseUniformImageFileName,
} from './uniformImageFileName';

const modalDetailRowSx = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 2,
    py: 0.5,
} as const;

const modalLabelColumnSx = {
    flex: '0 0 160px',
    minWidth: 0,
} as const;

function ModalDetailRow({ label, value }: { label: string; value: string }) {
    return (
        <Box sx={modalDetailRowSx}>
            <Typography component="span" variant="subtitle2" color="text.secondary" sx={modalLabelColumnSx}>
                {label}
            </Typography>
            <Typography component="span" variant="body1" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                {value}
            </Typography>
        </Box>
    );
}

export interface UniformGalleryImageModalProps {
    src: string;
    open: boolean;
    /** Full ordered list (e.g. filtered folder URLs) for prev/next navigation */
    orderedUrls: string[];
    onClose: () => void;
    onNavigate: (nextSrc: string) => void;
}

const UniformGalleryImageModal: React.FC<UniformGalleryImageModalProps> = ({
    src,
    open,
    orderedUrls,
    onClose,
    onNavigate,
}) => {
    const { t } = useTranslation();
    const { profile } = useProfile();

    const orderedUrlsRef = useRef(orderedUrls);
    const srcRef = useRef(src);
    const onNavigateRef = useRef(onNavigate);
    orderedUrlsRef.current = orderedUrls;
    srcRef.current = src;
    onNavigateRef.current = onNavigate;

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const onKeyDown = (e: KeyboardEvent) => {
            const urls = orderedUrlsRef.current;
            const current = srcRef.current;
            const i = urls.indexOf(current);
            if (e.key === 'ArrowLeft' && i > 0) {
                e.preventDefault();
                onNavigateRef.current(urls[i - 1]);
            } else if (e.key === 'ArrowRight' && i >= 0 && i < urls.length - 1) {
                e.preventDefault();
                onNavigateRef.current(urls[i + 1]);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open]);

    const name = fileNameFromImageUrl(src);
    const parsed = parseUniformImageFileName(name);
    const alt = parsed.kind === 'structured' ? `${parsed.plate}, ${parsed.driverName}` : name;
    const modalHeaderTitle = parsed.kind === 'structured' ? parsed.driverName : name;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            scroll="paper"
            aria-labelledby="uniform-gallery-image-dialog-title"
            PaperProps={{
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh',
                },
            }}
        >
            <Box
                id="uniform-gallery-image-dialog-title"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    backgroundColor: 'secondary.main',
                    position: 'sticky',
                    top: 0,
                    p: 0.5,
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    flexShrink: 0,
                    gap: 0.5,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, px: 0.5 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: palette.black,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {modalHeaderTitle}
                    </Typography>
                </Box>
                <IconButton
                    id="close-uniform-gallery-image-btn"
                    size="small"
                    onClick={onClose}
                    aria-label={String(t('table.close'))}
                    sx={{ color: palette.black }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                        maxHeight: '360px',
                        p: 1,
                        width: '460px',
                    }}
                >
                    {open ? (
                        <Box sx={{ width: '100%', maxWidth: '480px', p: 1, borderRadius: 1 }}>
                            <UniformInCabinGalleryThumb src={src} alt={alt} />
                        </Box>
                    ) : null}
                </Box>
                <Stack spacing={0} sx={{ pt: 0.5, width: '80%' }}>
                    {parsed.kind === 'structured' ? (
                        <>
                            <ModalDetailRow label={t('content.uniform.card_plate')} value={parsed.plate} />
                            <ModalDetailRow label={t('content.uniform.card_driver')} value={parsed.driverName} />
                            <ModalDetailRow label={t('content.uniform.card_internal_id')} value={parsed.internalId} />
                            <ModalDetailRow
                                label={t('content.uniform.card_captured')}
                                value={formatUniformCaptureUnixForDisplay(parsed.capturedUnix, profile)}
                            />
                        </>
                    ) : (
                        <ModalDetailRow label={t('content.uniform.file_name')} value={name} />
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default UniformGalleryImageModal;
